/**
 * Map NFL prop stat types to Today's Game scoring Lotties, and persist the last
 * progress value the user has already been shown.
 *
 * Touchdown props win over passing/rushing/receiving so `passing_touchdowns`
 * uses TOUCHDOWN, not What-A-Throw.
 */

export type PropCelebrationKind = 'touchdown' | 'rush' | 'catch' | 'pass';

export const PROP_CELEBRATION_LOTTIE_SRC: Record<PropCelebrationKind, string> = {
  touchdown: `${import.meta.env.BASE_URL}lottie/TOUCHDOWN.lottie`,
  rush: `${import.meta.env.BASE_URL}lottie/What-A-Run.lottie`,
  catch: `${import.meta.env.BASE_URL}lottie/What-A-Catch.lottie`,
  pass: `${import.meta.env.BASE_URL}lottie/What-A-Throw.lottie`,
};

const STORAGE_PREFIX = 'daily-reveal:prop-progress-seen';

export function buildPropProgressSeenKey(entryId: string, dayId: string): string {
  return `${STORAGE_PREFIX}:${entryId}:${dayId}`;
}

export function resolvePropCelebrationKind(
  statType: string | null | undefined
): PropCelebrationKind | null {
  if (!statType) return null;
  const t = statType.toLowerCase();

  if (t.includes('touchdown') || t === 'td' || t.endsWith('_td')) return 'touchdown';
  if (t.includes('rush')) return 'rush';
  if (t.includes('receiv') || t.includes('reception') || t.includes('catch')) return 'catch';
  if (t.includes('pass') || t.includes('completion')) return 'pass';
  return null;
}

/** Fallback when card prop metadata is missing (optimistic pick / label-only). */
export function inferCelebrationStatType(
  statType: string | null | undefined,
  displayLabel?: string | null
): string | null {
  if (statType) return statType;
  if (!displayLabel) return null;
  const t = displayLabel.toLowerCase();
  if (t.includes('touchdown') || /\btds?\b/.test(t)) return 'anytime_touchdown';
  if (t.includes('rush')) return 'rushing_yards';
  if (t.includes('receiv') || t.includes('reception') || /\brec\b/.test(t))
    return 'receiving_yards';
  if (t.includes('pass') || t.includes('completion')) return 'passing_yards';
  return null;
}

export function getPropProgressSeenValue(key: string): number | null {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

export function markPropProgressSeenValue(key: string, value: number): void {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    // Private mode — in-memory last-seen in the hook still prevents a tight replay loop.
  }
}

export type ResolvePropProgressCelebrationInput = {
  enabled: boolean;
  statType: string | null | undefined;
  currentValue: number;
  lastSeenValue: number | null;
  reducedMotion?: boolean;
};

export type ResolvePropProgressCelebrationResult = {
  kind: PropCelebrationKind | null;
  src: string | null;
  shouldPlay: boolean;
  /** Value the progress bar should show while the overlay is up (or immediately if not playing). */
  displayedValue: number;
};

/**
 * Decide whether to overlay a scoring Lottie and which bar value to hold.
 *
 * - lastSeen null + current > 0 → first return after a scoring event (play from 0)
 * - current > lastSeen → live (or return) increment (hold lastSeen, then fill)
 * - current <= 0 or already seen → no overlay
 */
export function resolvePropProgressCelebration(
  input: ResolvePropProgressCelebrationInput
): ResolvePropProgressCelebrationResult {
  const kind = input.enabled ? resolvePropCelebrationKind(input.statType) : null;
  const current = Number.isFinite(input.currentValue) ? Math.max(0, input.currentValue) : 0;

  if (!kind) {
    return { kind: null, src: null, shouldPlay: false, displayedValue: current };
  }

  const src = PROP_CELEBRATION_LOTTIE_SRC[kind];
  const skipMotion = Boolean(input.reducedMotion);

  if (input.lastSeenValue == null) {
    if (current <= 0) {
      return { kind, src, shouldPlay: false, displayedValue: 0 };
    }
    return {
      kind,
      src,
      shouldPlay: !skipMotion,
      displayedValue: skipMotion ? current : 0,
    };
  }

  if (current > input.lastSeenValue) {
    return {
      kind,
      src,
      shouldPlay: !skipMotion,
      displayedValue: skipMotion ? current : input.lastSeenValue,
    };
  }

  return { kind, src, shouldPlay: false, displayedValue: current };
}
